import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { errorToast } from "../../components/notifications/toast";
import Loader from "../../components/loaders/dot-loader";
import {
    dashboardAction, dashboardLowStockProductsAction,
    dashboardMontlySalesAction, dashboardOutOfStockProductsAction
} from "../../redux/actions/dashboardAction";
import { generateYearsList } from "../../components/helper";
import SelectDropdown from "../../components/forms/selectDropdown";
import CountUp from "react-countup";
import { motion } from "framer-motion";

const Dashboard = () => {
    const salesData = [
        { month: "Jan", sales: 0 },
        { month: "Feb", sales: 0 },
        { month: "Mar", sales: 0 },
        { month: "Apr", sales: 0 },
        { month: "May", sales: 0 },
        { month: "Jun", sales: 0 },
        { month: "Jul", sales: 0 },
        { month: "Aug", sales: 0 },
        { month: "Sep", sales: 0 },
        { month: "Oct", sales: 0 },
        { month: "Nov", sales: 0 },
        { month: "Dec", sales: 0 },
    ];

    const dispatch = useDispatch();
    const [dashboardData, setDashboardData] = useState({});
    const [monthlySalesData, setMonthlySalesData] = useState(salesData);
    const [outOfStockProducts, setOutOfStockProducts] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([])
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        dispatch(dashboardAction({}, onSuccess, onError));
        dispatch(dashboardOutOfStockProductsAction({}, onOutOfStockProdSuccess, onError))
        dispatch(dashboardLowStockProductsAction({}, onLowStockProdSuccess, onError))
    }, [])

    useEffect(() => {
        dispatch(dashboardMontlySalesAction({ year: year }, onMonthlySalesSuccess, onError))
    }, [year])

    const onSuccess = (data) => {
        setDashboardData(data.data.data);
        setLoading(false)
    }

    const onOutOfStockProdSuccess = (data) => {
        setOutOfStockProducts(data.data.data);
        setLoading(false)
    }

    const onLowStockProdSuccess = (data) => {
        setLowStockProducts(data.data.data);
        setLoading(false)
    }

    const onMonthlySalesSuccess = (data) => {
        setMonthlySalesData(data.data.data);
        setLoading(false)
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    if (loading) {
        return <div className="flex items-center justify-center" style={{ height: "95vh" }}>
            <Loader />
        </div>;
    }
    const yearOptions = generateYearsList();

    return (
        <div className="p-6 bg-background w-full overflow-auto" style={{ height: '95vh' }}>
            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn">
                            <h3 className="font-normal text-gray-700">Total Sales</h3>
                            <p className="text-xl font-bold text-primary">
                                ₹<CountUp end={dashboardData.totalSales} duration={2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-200">
                            <h3 className="font-normal text-gray-700">Total Revenue</h3>
                            <p className="text-xl font-bold text-green-600">
                                ₹<CountUp end={dashboardData.totalRevenue} duration={2.5} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-400">
                            <h3 className="font-normal text-gray-700">
                                Out of Stock Products
                            </h3>
                            <p className="text-xl font-bold text-red-500">
                                <CountUp end={dashboardData.outOfStock} duration={2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Low Stock Products
                            </h3>
                            <p className="text-xl font-bold text-amber-600">
                                <CountUp end={dashboardData.lowStock} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Initiated Routes
                            </h3>
                            <p className="text-xl font-bold text-amber-600">
                                <CountUp end={dashboardData.initiatedRoutes} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Dispatched Routes
                            </h3>
                            <p className="text-xl font-bold text-purple-600">
                                <CountUp end={dashboardData.dispatchedRoutes} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Completed Routes
                            </h3>
                            <p className="text-xl font-bold text-green-600">
                                <CountUp end={dashboardData.completedRoutes} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Pending Orders
                            </h3>
                            <p className="text-xl font-bold text-amber-600">
                                <CountUp end={dashboardData.pendingOrders} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Approved Orders
                            </h3>
                            <p className="text-xl font-bold text-green-600">
                                <CountUp end={dashboardData.approvedOrders} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Rejected Orders
                            </h3>
                            <p className="text-xl font-bold text-red-600">
                                <CountUp end={dashboardData.rejectedOrders} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Total Inventory Value(PTS)
                            </h3>
                            <p className="text-xl font-bold text-primary">
                                <CountUp end={dashboardData.totalInventoryValuePTS} duration={2.2} />
                            </p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded shadow animate-scaleIn delay-600">
                            <h3 className="font-normal text-gray-700">
                                Total Inventory Value(PTR)
                            </h3>
                            <p className="text-xl font-bold text-green-600">
                                <CountUp end={dashboardData.totalInventoryValuePTR} duration={2.2} />
                            </p>
                        </div>

                    </div>
                </div>

                {/* Monthly Sales Graph */}
                <div className="bg-white rounded shadow p-4">
                    <motion.div
                        className="bg-white rounded shadow p-4 h-full"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                    >
                        <h2 className="text-xl font-bold text-primary mb-2 text-start">Monthly Sales</h2>
                        <div className="mb-2 flex justify-end gap-3 items-center">
                            <label htmlFor="year" className="block font-medium text-primary">Year</label>
                            <div className="w-full max-w-28 sm:max-w-28 md:w-28">
                                <SelectDropdown
                                    options={yearOptions}
                                    value={year}
                                    onChange={(name, value) => setYear(value)}
                                    placeholder="Select year"
                                    name="category_id"
                                    className="input-field"
                                />
                            </div>
                        </div>

                        <ResponsiveContainer width="100%" height={350}>
                            <LineChart data={monthlySalesData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="sales"
                                    stroke="#2563EB"
                                    activeDot={{ r: 8 }}
                                    isAnimationActive={true}
                                    animationDuration={1500}
                                    animationEasing="ease-in-out"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </motion.div>
                </div>

                {/* Out of Stock Table */}
                <div className="bg-white rounded shadow p-4" style={{ minHeight: '30rem' }}>
                    <h2 className="text-xl font-bold text-red-600 pb-4 text-start">Out of Stock Products</h2>
                    <div style={{ maxHeight: 'calc(30rem - 2.5rem)', overflowY: 'auto' }}>
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 bg-white">
                                <tr>
                                    <th className="border-b p-2 text-gray-600">Code</th>
                                    <th className="border-b p-2 text-gray-600">Name</th>
                                    <th className="border-b p-2 text-gray-600">Category</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outOfStockProducts.map((product) => (
                                    <tr key={product.id}>
                                        <td className="p-2 border-b">{product.product.code}</td>
                                        <td className="p-2 border-b">{product.product.name}</td>
                                        <td className="p-2 border-b">{product.product.category.name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Low Stock Table */}
                <div className="bg-white rounded shadow p-4" style={{ minHeight: '30rem' }}>
                    <h2 className="text-xl font-bold text-amber-600 pb-4 text-start">Low Stock Products</h2>
                    <div style={{ maxHeight: 'calc(30rem - 2.5rem)', overflowY: 'auto' }}>
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 bg-white">
                                <tr>
                                    <th className="border-b p-2 text-gray-600">Code</th>
                                    <th className="border-b p-2 text-gray-600">Name</th>
                                    <th className="border-b p-2 text-gray-600">Category</th>
                                    <th className="border-b p-2 text-gray-600">Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lowStockProducts.map((product) => (
                                    <tr key={product.id}>
                                        <td className="p-2 border-b">{product.product.code}</td>
                                        <td className="p-2 border-b">{product.product.name}</td>
                                        <td className="p-2 border-b">{product.product.category.name}</td>
                                        <td className="p-2 border-b">{product.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;

